/**
 * Shared load metrics — rate-per-mile, the sortable-column comparator, and the display formatters for the
 * Rate / RPM / Miles / DH-O / DH-D columns. Extracted from LoadsTable/LoadRow so the load-hub table and the
 * box-truck board compute and sort these columns identically (single source of truth).
 */
import { CanonicalLoad } from '@numeo/types-core'
import { ratePerMileUsd } from '@numeo/types'
import type { SortColumn } from './types'

const getLoadRpm = (load: CanonicalLoad): number | null => ratePerMileUsd(load.rateAmountUsd, load.ratePerMileUsd, load.distanceMiles)

const numOrNull = (v: unknown): number | null => (v == null || isNaN(Number(v)) ? null : Number(v))

/**
 * Order two already-extracted column values, with NULLS ALWAYS LAST (regardless of direction).
 *
 * The one null rule for every sortable column on both boards. Exported because the box-truck board sorts by a
 * PRECOMPUTED key (see `sortByNumericKey`) rather than by calling a comparator that re-extracts the value on
 * every comparison — and the two must not be able to disagree about where nulls go.
 */
export const compareSortValues = (av: number | null, bv: number | null, dir: 'asc' | 'desc'): number => {
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    return dir === 'asc' ? av - bv : bv - av
}

/**
 * Compare two loads by a sortable column in the given direction, with NULLS ALWAYS LAST (regardless of
 * direction). Shared so the loads table and the box-truck board order these columns the exact same way.
 */
export const compareLoadsBy = (a: CanonicalLoad, b: CanonicalLoad, column: SortColumn, dir: 'asc' | 'desc'): number =>
    compareSortValues(loadSortValue(a, column), loadSortValue(b, column), dir)

/** The numeric value a sortable column ranks by (null → sorts last). */
export const loadSortValue = (load: CanonicalLoad, column: SortColumn): number | null => {
    switch (column) {
        case 'age':
            return load.createdAt ? new Date(load.createdAt).getTime() : null
        case 'rate':
            return numOrNull(load.rateAmountUsd)
        case 'rpm':
            return getLoadRpm(load)
        case 'dhO':
            return numOrNull(load.details?.origin_deadhead_miles)
        case 'dhD':
            return numOrNull(load.details?.destination_deadhead_miles)
        default:
            return null
    }
}

/** Built once: `Number.prototype.toLocaleString` constructs a formatter on every call, and this one runs per
 *  row per render of a virtualized board. Same locale, same options, same output. */
const WHOLE_DOLLARS_FMT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

/** "$1,250" — the load's total rate, or null when absent. */
export const formatRateUsd = (load: CanonicalLoad): string | null =>
    load.rateAmountUsd != null && !isNaN(Number(load.rateAmountUsd)) ? `$${WHOLE_DOLLARS_FMT.format(Number(load.rateAmountUsd))}` : null

/** "$2.35" — rate per mile (server value or derived), or null when it can't be computed. */
export const formatRpmUsd = (load: CanonicalLoad): string | null => {
    const rpm = getLoadRpm(load)
    return rpm == null ? null : `$${rpm.toFixed(2)}`
}
